import { msg } from "@lingui/core/macro";
import {
  Archive,
  ArrowUp,
  BellPlus,
  ChevronDown,
  Clock,
  Loader2,
  SendHorizontal,
} from "lucide-react";
import * as React from "react";

import { cn } from "../../lib/cn";
import { useI18n } from "../../lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "../../primitives/dropdown-menu";

/**
 * SendControl — the premium split-button that anchors the reply compose.
 *
 * Left segment: the primary "Invia" action (the ONE sanctioned orange accent
 * on the surface — a warm gradient with a Lucide arrow-up). Right segment: a
 * caret opening a pencil dropdown with the secondary send modes (schedule /
 * send-and-archive / follow-up reminder), each conditionally rendered only
 * when the host wires the matching handler.
 *
 * Keyboard (scoped to the nearest ``[data-email-compose]`` ancestor so it
 * never fires from another composer mounted elsewhere on the page):
 *   - ⌘/Ctrl + Enter          → ``onSend``
 *   - ⌘/Ctrl + Shift + Enter  → ``onSendArchive`` (when wired)
 *
 * When a deferred send is scheduled the host passes ``scheduledLabel`` and the
 * primary segment surfaces a compact chip so the user always sees WHEN it goes.
 */

export interface SendControlProps {
  /** Primary send. Fired by click and by ⌘⏎. */
  readonly onSend: () => void;
  /** Opens the host's existing scheduler. Menu item hidden when omitted. */
  readonly onSchedule?: () => void;
  /** Send then archive the thread. Fired by ⌘⇧⏎. Menu item hidden when omitted. */
  readonly onSendArchive?: () => void;
  /** Create a follow-up reminder. Menu item hidden when omitted. */
  readonly onRemind?: () => void;
  readonly disabled?: boolean;
  /** Spinner + inert primary while a send is in flight (or in its undo window). */
  readonly busy?: boolean;
  /** Short "programmata per …" label — renders a chip on the primary segment. */
  readonly scheduledLabel?: string;
  /** Overrides the primary label (defaults to "Invia" / "Programma" when scheduled). */
  readonly primaryLabel?: string;
  readonly className?: string;
}

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD_SYMBOL = isMac ? "⌘" : "Ctrl";

export const SendControl: React.FC<SendControlProps> = ({
  onSend,
  onSchedule,
  onSendArchive,
  onRemind,
  disabled,
  busy,
  scheduledLabel,
  primaryLabel,
  className,
}) => {
  const { i18n } = useI18n();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  const inert = Boolean(disabled) || Boolean(busy);

  // Keep the latest handlers in a ref so the document listener stays stable
  // (registered once) yet always calls the current closures.
  const handlersRef = React.useRef({ onSend, onSendArchive, inert });
  handlersRef.current = { onSend, onSendArchive, inert };

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || !(e.metaKey || e.ctrlKey)) return;
      // Scope to THIS compose: the event must originate inside the same
      // ``[data-email-compose]`` subtree that hosts this control, so a ⌘⏎ in
      // the chat composer (or any other surface) never sends the email.
      const scope = rootRef.current?.closest("[data-email-compose]");
      const target = e.target as Node | null;
      if (!scope || !target || !scope.contains(target)) return;
      const { onSend: send, onSendArchive: sendArchive, inert: isInert } = handlersRef.current;
      if (isInert) return;
      e.preventDefault();
      if (e.shiftKey) {
        if (sendArchive) sendArchive();
        return;
      }
      send();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const hasMenu = Boolean(onSchedule || onSendArchive || onRemind);
  const label = primaryLabel ?? (scheduledLabel ? i18n._(msg`Programma`) : i18n._(msg`Invia`));

  return (
    <div ref={rootRef} className={cn("inline-flex items-stretch", className)}>
      {/* Primary segment — the single orange accent on the surface. */}
      <button
        type="button"
        onClick={onSend}
        disabled={inert}
        aria-label={scheduledLabel ? i18n._(msg`Programma invio`) : i18n._(msg`Invia email`)}
        aria-keyshortcuts="Meta+Enter Control+Enter"
        className={cn(
          "inline-flex items-center gap-1.5 bg-[linear-gradient(180deg,#ff8f3f_0%,#f2700f_100%)] px-3 py-1.5 font-semibold text-[12.5px] text-white shadow-sm transition-[filter,opacity] hover:brightness-[1.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60",
          hasMenu ? "rounded-l-md" : "rounded-md",
        )}
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : scheduledLabel ? (
          <Clock className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
        ) : (
          <ArrowUp className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
        )}
        <span>{label}</span>
        {scheduledLabel ? (
          <span className="ml-0.5 rounded-full bg-white/20 px-1.5 py-px text-[10.5px] tabular-nums">
            {scheduledLabel}
          </span>
        ) : null}
      </button>

      {hasMenu ? (
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={inert}
              aria-label={i18n._(msg`Altre opzioni di invio`)}
              className="inline-flex items-center rounded-r-md border-white/25 border-l bg-[linear-gradient(180deg,#ff8f3f_0%,#f2700f_100%)] px-1.5 text-white transition-[filter,opacity] hover:brightness-[1.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 transition-transform motion-reduce:transition-none",
                  menuOpen ? "rotate-180" : "rotate-0",
                )}
                strokeWidth={2.5}
                aria-hidden
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={6} className="min-w-[248px]">
            <DropdownMenuItem onSelect={() => onSend()}>
              <SendHorizontal className="h-4 w-4 text-ink-3" aria-hidden />
              <span className="text-[12.5px] text-ink-1">{i18n._(msg`Invia ora`)}</span>
              <DropdownMenuShortcut>{MOD_SYMBOL}↵</DropdownMenuShortcut>
            </DropdownMenuItem>
            {onSchedule ? (
              <DropdownMenuItem onSelect={() => onSchedule()}>
                <Clock className="h-4 w-4 text-ink-3" aria-hidden />
                <span className="text-[12.5px] text-ink-1">{i18n._(msg`Programma…`)}</span>
              </DropdownMenuItem>
            ) : null}
            {onSendArchive ? (
              <DropdownMenuItem onSelect={() => onSendArchive()}>
                <Archive className="h-4 w-4 text-ink-3" aria-hidden />
                <span className="text-[12.5px] text-ink-1">{i18n._(msg`Invia e archivia`)}</span>
                <DropdownMenuShortcut>{MOD_SYMBOL}⇧↵</DropdownMenuShortcut>
              </DropdownMenuItem>
            ) : null}
            {onRemind ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => onRemind()}>
                  <BellPlus className="h-4 w-4 text-ink-3" aria-hidden />
                  <span className="text-[12.5px] text-ink-1">
                    {i18n._(msg`Ricordami se non rispondono`)}
                  </span>
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
};
SendControl.displayName = "SendControl";
